import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { KeycloakService } from 'keycloak-angular';
import { SideNavbarComponent } from '../../../../my-shared-lib/src/lib/chart-components/side-navbar/side-navbar.component';
import { ConfigService } from '../../../../my-shared-lib/src/lib/shared-services/config.service';
import { GlobalService } from '../../../../my-shared-lib/src/lib/shared-services/global.service';
// import { GlobalFilterComponent } from '../../../components/global-filter/global-filter.component';

@Component({
  selector: 'app-landing-page',
  standalone: true,
  imports: [CommonModule, MatIconModule, SideNavbarComponent],
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
})
export class LandingPageComponent implements OnInit, OnDestroy {
  menuVisible = false;
  selectedLabel: any;
  private hideMenuTimer: any;
  menuPosition: string = 'top-right';
  menu: any;
  allowedPaths: string[] = [];
  currentUserRole = '';
  quarterYear = '';
  private readonly destroy$ = new Subject<void>();
  private lastPlatformSelection = '';

  constructor(
    private router: Router,
    private service: ConfigService,
    private filterState: GlobalService,
    private keycloakService: KeycloakService
  ) { }

  ngOnInit(): void {
    this.loadRoleMenuAccess();

    this.service.getLandingPageEngineModels().subscribe({
      error: (error) => {
        console.error('Landing page engine model API failed', error);
      },
    });

    this.service.getQuadrant().subscribe({
      next: (data: any) => {
        this.quarterYear = data?.quarterYear ?? '';
      },
      error: (error) => {
        console.error('Landing page current-year API failed', error);
      },
    });

    this.filterState.filters$
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload: any) => {
        const selectedPlatforms = payload?.filter?.final_em;
        const normalizedSelection = Array.isArray(selectedPlatforms)
          ? selectedPlatforms.filter((value: any) => value !== 'All')
          : [];
        const currentKey = normalizedSelection.join('|');

        if (currentKey === this.lastPlatformSelection) {
          return;
        }

        this.lastPlatformSelection = currentKey;

        // Keep landing page in sync with platform dropdown selection changes.
        this.service
          .fetchData(
            '/spriced/platform',
            'DropdownEngineModelRead',
            payload?.filter || {},
            {}
          )
          .subscribe({
            error: (error) => {
              console.error('Landing page platform selection API failed', error);
            },
          });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isPinned = false;

  onQuadrantEnter(name: string) {
    // Always activate hovered quadrant
    this.showMenu(name);

    // If already pinned, keep it pinned but switch to new quadrant
    if (this.isPinned) {
      this.isPinned = true;
    }
  }

  onQuadrantClick(name: string, event: MouseEvent) {
    event.stopPropagation();

    if (this.isPinned && this.selectedLabel === name) {
      // Clicking same pinned menu closes it
      this.isPinned = false;
      this.menuVisible = false;
    } else {
      // Pin the new menu
      this.isPinned = true;
      this.showMenu(name);
    }
  }

  onMouseLeave() {
    if (this.isPinned) return; // don't hide if pinned
    this.hideMenuTimer = setTimeout(() => {
      this.menuVisible = false;
    }, 200);
  }

  onMenuEnter() {
    if (this.hideMenuTimer) {
      clearTimeout(this.hideMenuTimer);
    }
  }

  private showMenu(name: string) {
    this.menu = this.filterMenu(this.service.getMenuItems());
    if (this.hideMenuTimer) {
      clearTimeout(this.hideMenuTimer);
    }
    this.selectedLabel = name;
    this.menuVisible = true;

    switch (name) {
      case 'Product Health':
        this.menuPosition = 'top-left';
        break;
      case 'Ideation':
        this.menuPosition = 'top-right';
        break;
      case 'Product Improvement':
        this.menuPosition = 'bottom-left';
        break;
      case 'Current Projects':
        this.menuPosition = 'bottom-right';
        break;
    }
  }

  private async loadRoleMenuAccess(): Promise<void> {
    const isLoggedIn = await this.keycloakService.isLoggedIn();
    if (!isLoggedIn) return;

    const email = this.keycloakService.getKeycloakInstance().idTokenParsed?.['email'];
    if (!email) return;

    this.service.getRole(email).subscribe((role: any) => {
      this.currentUserRole = role || '';
      this.service.getMenu(role).subscribe((paths: any) => {
        this.allowedPaths = Array.isArray(paths) ? paths : [];
        this.menu = this.filterMenu(this.service.getMenuItems());
      });
    });
  }

  private filterMenu(menu: any): any {
    const allowed = this.allowedPaths || [];
    const normalize = (path: string) => path?.startsWith('/') ? path : `/${path}`;

    const filterRecursive = (items: any[]): any[] => {
      return (items || [])
        .filter((item) => !Array.isArray(item.hiddenRoles) || !item.hiddenRoles.includes(this.currentUserRole))
        .filter((item) => !item.path || allowed.includes(normalize(item.path)))
        .map((item) => ({
          ...item,
          children: item.children ? filterRecursive(item.children) : null,
        }))
        .filter((item) => !Array.isArray(item.children) || item.children.length > 0);
    };

    return Object.keys(menu || {}).reduce((result: any, key) => {
      result[key] = filterRecursive(menu[key]);
      return result;
    }, {});
  }

  navigate(item: any) {
    if (!item?.path) {
      return;
    }

    if (item.dynamicChildren === 'openSurveyTypes' || item.path === '/survey') {
      this.navigateToSurveyIfOpen(item.path);
      return;
    }

    this.menuVisible = false;
    this.router.navigate([item.path]); // Angular Router navigation
  }

  private navigateToSurveyIfOpen(path: string): void {
    this.service.getDashboardConfig('settings/list-of-survey.json').subscribe({
      next: (config: any) => {
        const tableConfig = this.findSurveyListTableConfig(config);
        const readAction = this.resolveSurveyReadAction(tableConfig);
        if (!readAction?.payload) {
          return;
        }

        const payload = this.replacePayloadTokens(readAction.payload);
        this.service
          .customApi(
            readAction.url || tableConfig?.apiUrl || '/spriced/platform',
            payload,
            tableConfig,
            readAction.method || 'post'
          )
          .subscribe({
            next: (response: any) => {
              if (!this.hasPublishedSurveys(response, tableConfig)) {
                return;
              }

              localStorage.removeItem('selectedSurveyType');
              this.menuVisible = false;
              this.router.navigate([path]);
            },
            error: (error) => {
              console.error('Failed to load published surveys from landing page', error);
            },
          });
      },
      error: (error) => {
        console.error('Failed to load survey configuration from landing page', error);
      },
    });
  }

  private findSurveyListTableConfig(config: any): any {
    const rows = Array.isArray(config?.pageContent) ? config.pageContent : [];
    for (const row of rows) {
      const columns = Array.isArray(row?.columns) ? row.columns : [];
      const tableConfig = columns.find(
        (column: any) => column?.widgetId === 'surveyListTable'
      );
      if (tableConfig) {
        return tableConfig;
      }
    }

    return null;
  }

  private resolveSurveyReadAction(tableConfig: any): any {
    if (tableConfig?.initialLoadAction?.payload) {
      return tableConfig.initialLoadAction;
    }

    const actionIndex = Number(tableConfig?.initialLoadPostSaveActionIndex);
    if (
      Number.isInteger(actionIndex) &&
      Array.isArray(tableConfig?.postSaveActions)
    ) {
      return tableConfig.postSaveActions[actionIndex];
    }

    return Array.isArray(tableConfig?.postSaveActions)
      ? tableConfig.postSaveActions[0]
      : null;
  }

  private replacePayloadTokens(value: any): any {
    if (Array.isArray(value)) {
      return value.map((item) => this.replacePayloadTokens(item));
    }

    if (value && typeof value === 'object') {
      return Object.entries(value).reduce(
        (acc: Record<string, any>, [key, item]) => {
          acc[key] = this.replacePayloadTokens(item);
          return acc;
        },
        {}
      );
    }

    if (typeof value !== 'string') {
      return value;
    }

    const currentYear = new Date().getFullYear();
    if (value === '__YEAR__' || value === '__CURRENT_YEAR__') {
      return currentYear;
    }

    return value.replace(/__YEAR__|__CURRENT_YEAR__/g, String(currentYear));
  }

  private hasPublishedSurveys(response: any, tableConfig: any): boolean {
    const responseData = response?.responseList?.[0]?.responseData;
    const primaryPayload = Array.isArray(responseData) ? responseData[0] : responseData;
    const respKey = tableConfig?.respKey;
    const candidates = respKey
      ? [
          primaryPayload?.[respKey],
          response?.responseList?.[0]?.[respKey],
          response?.[respKey],
          responseData,
          response?.responseData,
        ]
      : [responseData, response?.responseData, primaryPayload, response];

    const rows = candidates.find((candidate) => Array.isArray(candidate)) || [];
    const today = new Date();
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();

    return rows.some((row: any) => {
      const status = String(row?.status || '').trim().toLowerCase();
      const isPublished = status === 'published';
      const startDate = this.toDateOnly(row?.start_date);
      const endDate = this.toDateOnly(row?.end_date);
      return (
        isPublished &&
        (!startDate || startDate.getTime() <= todayOnly) &&
        (!endDate || endDate.getTime() >= todayOnly)
      );
    });
  }

  private toDateOnly(value: any): Date | null {
    if (!value) {
      return null;
    }

    if (typeof value === 'string') {
      const datePart = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (datePart) {
        return new Date(Number(datePart[1]), Number(datePart[2]) - 1, Number(datePart[3]));
      }
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  }
}
